import autopitchLogo from "@/assets/autopitch-logo.png";
import { LiveDivider } from "@/components/motion/LiveDivider";
import { Container } from "@/components/layout/Container";
import { ShieldCheck, Globe2, FileLock2 } from "lucide-react";

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "SOC 2", sub: "In progress" },
  { icon: Globe2, label: "GDPR", sub: "Compliant" },
  { icon: FileLock2, label: "DPA", sub: "Available" },
];

const RESOURCE_LINKS = [
  { href: "/pricing", label: "Pricing" },
  { href: "/best-ai-sdr-tools", label: "Best AI SDR Tools" },
  { href: "/what-is-ai-sdr", label: "What is an AI SDR?" },
  { href: "/how-ai-sdrs-work", label: "How AI SDRs Work" },
  { href: "/smartlead-alternatives", label: "Smartlead Alternatives" },
  { href: "/autopitch-vs-smartlead", label: "AutoPitch vs Smartlead" },
  { href: "/autopitch-vs-apollo", label: "AutoPitch vs Apollo" },
  { href: "/book-more-meetings-with-ai", label: "Book More Meetings" },
  { href: "/ai-sdr-roi-calculator", label: "ROI Calculator" },
  { href: "/blog", label: "Blog" },
];

const LEGAL_LINKS = [
  { href: "/legal/dpa", label: "DPA" },
  { href: "/legal/subprocessors", label: "Sub-processors" },
  { href: "mailto:bryan@autopitch.io", label: "Privacy contact" },
];

interface LandingFooterProps {
  showLogo?: boolean;
}

export function LandingFooter({ showLogo = true }: LandingFooterProps) {
  return (
    <footer className="relative border-t border-border/60 bg-card/30 backdrop-blur-xl">
      <LiveDivider className="absolute top-0 left-0 right-0 -translate-y-1/2" />
      <Container size="wide" className="py-10 md:py-12 space-y-6">
        {showLogo && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src={autopitchLogo}
                alt="Auto Pitch"
                className="h-7 w-7 object-contain rounded-md"
                style={{ animation: "breathe-soft 4s ease-in-out infinite" }}
              />
              <span className="text-sm font-semibold text-foreground">Auto Pitch</span>
            </div>
            <p className="text-[10px] sm:text-[11px] text-muted-foreground font-mono text-center sm:text-right">
              © {new Date().getFullYear()} AUTO PITCH · ALL RIGHTS RESERVED
            </p>
          </div>
        )}
        <div className={showLogo ? "border-t border-border/50 pt-6" : ""}>
          <p className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 text-center sm:text-left">
            Resources
          </p>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap justify-items-start sm:justify-start gap-x-4 gap-y-2">
            {RESOURCE_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors story-link min-h-9 inline-flex items-center"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
        <div className="border-t border-border/50 pt-6 space-y-4">
          <p className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center sm:text-left">
            Legal
          </p>
          <div className="flex flex-wrap justify-center sm:justify-start gap-x-4 gap-y-2">
            {LEGAL_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors story-link min-h-9 inline-flex items-center"
              >
                {link.label}
              </a>
            ))}
          </div>
          <div className="flex flex-wrap justify-center sm:justify-start gap-2">
            {TRUST_BADGES.map(({ icon: Icon, label, sub }) => (
              <div
                key={label}
                className="inline-flex items-center gap-2 rounded-md border border-border/60 bg-background/40 px-2.5 py-1.5"
              >
                <Icon className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                <span className="text-[11px] font-mono uppercase tracking-wider text-foreground">
                  {label}
                </span>
                <span className="text-[10px] text-muted-foreground">{sub}</span>
              </div>
            ))}
          </div>
        </div>
        {!showLogo && (
          <p className="text-[11px] text-muted-foreground text-center">
            © {new Date().getFullYear()} Auto Pitch. All rights reserved.
          </p>
        )}
      </Container>
    </footer>
  );
}
